import styles from '../PdfRenderer.module.scss';
import {NewButton} from "../../../ui/NewButton/NewButton";
import {EditorComponent} from "../../../../EditorComponent";
import {stripHtml} from '../helpers/formatting';
import { PDFNoteCode } from '../../../../../pages/notes/models/PDFNoteCode';
import { PDFNoteCodeTypeVals } from '../../../../../helpers/mapping/PDFNoteCodeTypeVals';
import { formatPDFNoteCodeLabel } from '../../../../../helpers/mapping/formatPDFNoteCodeLabel';

interface CommentModalProps {
    title: string;
    quote?: string;
    comment: string;
    onCommentChange: (html: string) => void;
    onSave: () => void;
    onCancel: () => void;
    onDelete?: () => void;
    submitLabel?: string;
    submitIcon?: string;
    // Link-to-existing
    onLinkToExisting?: () => void;
    linkedNote?: { id: number; title: string } | null;
    onResetLink?: () => void;
    onSaveLinked?: () => void;
    // Критическое / код
    isCritical?: boolean;
    onIsCriticalChange?: (val: boolean) => void;
    codeId?: number;
    onCodeIdChange?: (val: number | undefined) => void;
    codes?: PDFNoteCode[];
    isExecutive?: boolean;
    showCriticalCode?: boolean;
}

export function CommentModal({
    title,
    quote,
    comment,
    onCommentChange,
    onSave,
    onCancel,
    onDelete,
    submitLabel = 'Сохранить',
    submitIcon = 'fa-solid fa-paper-plane',
    onLinkToExisting,
    linkedNote,
    onResetLink,
    onSaveLinked,
    isCritical,
    onIsCriticalChange,
    codeId,
    onCodeIdChange,
    codes,
    isExecutive,
    showCriticalCode,
}: CommentModalProps) {
    const hasText = stripHtml(comment);
    const isLinked = !!linkedNote;
    const codeRequired = showCriticalCode !== false && onCodeIdChange !== undefined;
    const missingCode = codeRequired && !codeId;

    return (
        <div className={styles.modalBackdrop} onClick={onCancel}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>{title}</span>
                    <button
                        type="button"
                        className={styles.modalClose}
                        onClick={onCancel}
                        aria-label="Закрыть"
                    >
                        <i className="fa-solid fa-xmark" />
                    </button>
                </div>
                {quote && (
                    <blockquote className={styles.modalQuote}>{quote}</blockquote>
                )}

                {isLinked ? (
                    <div className={styles.linkedNotePreview}>
                        <i className="fa-solid fa-link" />
                        <span className={styles.linkedNoteTitle}>
                            {stripHtml(linkedNote!.title) || `Замечание #${linkedNote!.id}`}
                        </span>
                        {onResetLink && (
                            <button type="button" className={styles.linkedNoteReset} onClick={onResetLink}>
                                Сбросить
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {(onIsCriticalChange || onCodeIdChange) && showCriticalCode !== false && (
                            <div className={styles.modalCriticalRow}>
                                <label className={styles.modalCheckboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={isCritical ?? false}
                                        onChange={(e) => onIsCriticalChange?.(e.target.checked)}
                                    />
                                    Критическое
                                </label>
                                <select
                                    className={styles.modalCodeSelect}
                                    value={codeId ?? ''}
                                    onChange={(e) => onCodeIdChange?.(e.target.value ? Number(e.target.value) : undefined)}
                                >
                                    <option value="">Выберите код</option>
                                    {(codes ?? []).map((c) => (
                                        <option key={c.Id} value={c.Id}>
                                            {formatPDFNoteCodeLabel(c, isExecutive ? PDFNoteCodeTypeVals.Executive : PDFNoteCodeTypeVals.Ordinary)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {/* Высота через CSS (.modalEditorWrap): меньше на низких экранах, сжимается
                            при нехватке места; скролл внутри редактора, футер всегда виден. */}
                        <div className={styles.modalEditorWrap}>
                            <EditorComponent
                                value={comment}
                                onTextChange={(e) => onCommentChange(e.htmlValue ?? '')}
                                className={styles.modalEditor}
                                style={{ height: '100%', minHeight: 0 }}
                            />
                        </div>
                        {onLinkToExisting && (
                            <button type="button" className={styles.linkToExistingBtn} onClick={onLinkToExisting}>
                                <i className="fa-solid fa-link" />
                                Связать с существующим замечанием
                            </button>
                        )}
                    </>
                )}

                <div className={styles.modalActions}>
                    {onDelete && (
                        <NewButton
                            variant="danger"
                            icon="fa-solid fa-trash"
                            onClick={onDelete}
                        >
                            Удалить
                        </NewButton>
                    )}
                    <span className={styles.modalActionsSpacer} />
                    <NewButton variant="secondary" onClick={onCancel}>
                        Отмена
                    </NewButton>
                    <NewButton
                        variant="primary"
                        icon={isLinked ? 'fa-solid fa-link' : submitIcon}
                        disabled={isLinked ? false : (!hasText || missingCode)}
                        onClick={isLinked ? onSaveLinked : onSave}
                    >
                        {submitLabel}
                    </NewButton>
                </div>
            </div>
        </div>
    );
}
